import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material'
import { CheckCircle as ApproveIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material'
import PageHelpButton from '../../components/PageHelpButton'
import { usePortalData } from '../../components/portal/PortalDataProvider'
import { ApiError, northStarApi, publishApi, NorthStarRecord } from '../../services/api'
import AttachmentsTab from '../../components/client-detail/AttachmentsTab'
import type { PortalSectionProps } from '../../components/portal/portalSection'
import { useNotification } from '../../hooks/useNotification'
import { isNorthStarClientApproved } from '../../utils/northStarApproval'

export default function PortalNorthStar({ embedded }: PortalSectionProps = {}) {
  const { clientId } = usePortalData()
  const [northStar, setNorthStar] = useState<NorthStarRecord | null>(null)
  // Failing to read the document is not failing the page: the files below are
  // a separate source and still render.
  const [documentFailed, setDocumentFailed] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  const [approving, setApproving] = useState(false)
  // The North Star changed while the approval was being recorded, so what is
  // on screen may no longer be the version that would be approved.
  const [approvalConflict, setApprovalConflict] = useState(false)
  const { showSuccess, showError } = useNotification()

  // The North Star document itself. The backend answers a client with the
  // published record only, so an unpublished draft arrives as no record at all.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setDocumentFailed(false)
      try {
        const resp = await northStarApi.get(clientId)
        if (!cancelled) {
          setNorthStar(resp?.northStar ?? null)
          // Cleared only once the fresh record is in: clearing it before the
          // fetch put the Approve button back on the stale version, and a
          // click in that window was refused with a second conflict.
          setApprovalConflict(false)
        }
      } catch (err) {
        console.error('Failed to load the North Star document:', err)
        if (!cancelled) setDocumentFailed(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clientId, retryKey])

  const published = northStar?.isPublished && northStar.slug ? northStar : null
  const approved = isNorthStarClientApproved(published)

  const handleApprove = async () => {
    if (!published) return
    setApproving(true)
    try {
      const result = await publishApi.approveNorthStar(clientId, published.version)
      setNorthStar((prev) =>
        prev
          ? { ...prev, clientApprovedVersion: result.clientApprovedVersion, clientApprovedAt: result.clientApprovedAt }
          : prev
      )
      showSuccess('North Star approved.')
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setApprovalConflict(true)
      } else {
        console.error('Failed to approve the North Star:', err)
        showError('Could not approve your North Star. Please try again.')
      }
    } finally {
      setApproving(false)
    }
  }

  return (
    <Box>
      {/* Suppressed when this renders as a tab of My Profile, which supplies
          the page heading. */}
      {!embedded && (
        <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">
          North Star
        </Typography>
        <PageHelpButton articleId="page-portal-north-star" />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your North Star strategy document.
      </Typography>
        </>
      )}

      <Stack spacing={2}>
        {documentFailed && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setRetryKey((k) => k + 1)}>
                Retry
              </Button>
            }
          >
            We could not check whether your North Star document is ready. Any files are still listed below.
          </Alert>
        )}

        {published && (
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Typography variant="h6">Your North Star is ready</Typography>
                {approved && (
                  <Chip icon={<ApproveIcon sx={{ fontSize: 16 }} />} label="Approved" color="success" />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {published.publishedAt
                  ? `Published ${new Date(published.publishedAt).toLocaleDateString()}. It opens in a new tab, where you can print it or save it as a PDF.`
                  : 'It opens in a new tab, where you can print it or save it as a PDF.'}
              </Typography>
              <Button
                component="a"
                href={`/north-star/${published.slug}`}
                target="_blank"
                rel="noopener"
                variant="contained"
                endIcon={<OpenInNewIcon />}
                sx={{ mt: 2 }}
              >
                Open your North Star
              </Button>

              {approved ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {published.clientApprovedAt
                    ? `You approved this version on ${new Date(published.clientApprovedAt).toLocaleDateString()}.`
                    : 'You approved this version.'}
                </Typography>
              ) : approvalConflict ? (
                <Alert
                  severity="warning"
                  sx={{ mt: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => setRetryKey((k) => k + 1)}>
                      Reload
                    </Button>
                  }
                >
                  Your North Star was just updated. Reload to review the latest version.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: { xs: 1.5, sm: 2 },
                      width: '100%',
                    }}
                  >
                    <Typography variant="body2">
                      Please review your North Star and approve it when you're satisfied.
                    </Typography>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={approving ? <CircularProgress size={14} /> : <ApproveIcon />}
                      onClick={handleApprove}
                      disabled={approving}
                      sx={{ flexShrink: 0 }}
                    >
                      Approve this version
                    </Button>
                  </Box>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <AttachmentsTab
              entityId={clientId}
              entityType="north_star"
              title={published ? 'North Star files' : 'North Star Document'}
              emptyStateText={
                published
                  ? 'There are no extra files with your North Star.'
                  : 'Your North Star is being built. It will appear here when it is ready.'
              }
              hideUpload
            />
          </CardContent>
        </Card>
      </Stack>
    </Box>
  )
}
